'use client';
import type { AccordionElementPropertiesModel } from '@/client';
import { AccordionModulePropertiesModel } from '@/client';
import { HeadingTag, ModuleBase, RichText, Text } from '@/components';
import classNames from 'classnames';
import { useState } from 'react';
import Chevron from '@/assets/chevron.svg';

const AccordionModule = ({ data, moduleName }: { data: AccordionModulePropertiesModel; moduleName: string }) => {
  const [activeIndexList, setActiveIndexList] = useState<number[]>([]);

  const toggle = (index: number) => {
    let newArray = [...activeIndexList];
    if (newArray.includes(index)) {
      newArray = newArray.filter((i) => i !== index);
    } else {
      if (data.allowOpeningMultiple) {
        newArray = [index, ...newArray];
      } else {
        newArray = [index];
      }
    }
    setActiveIndexList(newArray);
  };

  return (
    <ModuleBase data={data} moduleName={moduleName}>
      <div className="container">
        <div className="title-wrapper" data-cy="title-wrapper">
          {data.preHeading && (
            <Text
              className="text-xs font-semibold uppercase text-white/50 md:text-sm lg:mt-4"
              data-cy="accordion-pretitle"
            >
              {data.preHeading}{' '}
            </Text>
          )}
          {data.headingTitle && (
            <HeadingTag
              data={{ heading: data.headingTitle }}
              className="font-inter mt-7 text-3xl md:text-5xl"
              data-cy="accordion-heading"
            />
          )}
        </div>

        {data.accordionElements && data.accordionElements.items && (
          <div className="mt-10 text-black">
            {data.accordionElements?.items.map((item, index) => {
              const { title, textField } = item?.content?.properties as AccordionElementPropertiesModel;
              const isActive = activeIndexList.includes(index);

              return (
                <div
                  key={index}
                  className={`border-b transition-colors duration-500 ${isActive ? 'border-black/75' : 'border-black/25'}`}
                >
                  <button
                    type="button"
                    className="flex w-full items-center justify-between py-4 pr-4 text-base font-semibold md:text-lg"
                    onClick={() => toggle(index)}
                    data-cy="accordion-button"
                  >
                    <span>{title}</span>
                    <Chevron
                      className={`size-4 transition-transform duration-500 ${isActive ? 'rotate-180' : 'rotate-0'}`}
                      data-cy="chevron"
                    />
                  </button>

                  <div
                    className={classNames(
                      'grid transition-all duration-500 ease-out',
                      isActive ? 'grid-rows-[1fr] pb-4' : 'grid-rows-[0fr] pb-0',
                    )}
                  >
                    {textField && (
                      <RichText
                        data={textField}
                        className="richtext-content overflow-hidden"
                        data-cy="accordion-text"
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </ModuleBase>
  );
};

export default AccordionModule;
